import { Component, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { Crypto } from 'src/app/service/classes/crypto/crypto';

interface SegurancaResponse {
  msg: string;
  success: boolean;
  timestamp: number;
  result: Array<{ email: string }>;
}

@Component({
  selector: 'app-esocial',
  template: `
    <div style="text-align:center; padding: 50px 20px;">
      <h3 aria-live="polite">🔗 Conectando ao eSocial...</h3>
      <p>Por favor, aguarde enquanto redirecionamos você.</p>

      <button
        id="navegar"
        class="btn btn-primary"
        style="display:none"
        (click)="enviarFormulario()"
        aria-hidden="true"
        tabindex="-1"
      >
        Acessar eSocial
      </button>

      <div style="margin-top:24px">
        <p style="margin-bottom:8px">
          Se você <strong>não foi redirecionado</strong>, desative o bloqueio de pop-ups ou clique
          no botão abaixo para tentar novamente.
        </p>

        <button class="btn btn-secondary" (click)="enviarFormulario()" [disabled]="tentando">
          {{ tentando ? 'Tentando…' : 'Tentar novamente' }}
        </button>

        <div *ngIf="erro" style="margin-top:12px; color:#b00020">
          {{ erro }}
        </div>
      </div>
    </div>
  `,
})
export class EsocialComponent implements AfterViewInit {
  private readonly apiRoot = 'https://api-relatorios-php.elogica.info';

  private readonly basicUser = '3l0g1k4';
  private readonly basicPass = 'dtr5x8!qAa6>';

  tentando = false;
  erro: string | null = null;

  constructor(private router: Router, private http: HttpClient) {
    this.verificarAutenticacao();
  }

  ngAfterViewInit(): void {
    this.enviarFormulario();
  }

  private verificarAutenticacao(): void {
    const util = this.getUtilDecodificado();
    if (!util) {
      this.router.navigate(['/login']);
      return;
    }
    const temCodigo = !!util?.CodigoUsuario;
    const temLoginSUsuario = !!util?.LoginSUsuario;
    if (!temCodigo || !temLoginSUsuario) {
      this.router.navigate(['/login']);
    }
  }

  async enviarFormulario(): Promise<void> {
    this.tentando = true;
    this.erro = null;

    try {
      const util = this.getUtilDecodificado();
      if (!util) throw new Error('Credencial não encontrada. Faça login novamente.');

      const codigoUsuario = String(util.CodigoUsuario || '').trim();
      const loginSUsuario = String(util.LoginSUsuario || '').trim();
      if (!codigoUsuario || !loginSUsuario) {
        throw new Error('Dados do usuário incompletos. Faça login novamente.');
      }

      const email = await this.buscarEmail(codigoUsuario);
      if (!email) {
        throw new Error('Não foi possível obter o e-mail do usuário.');
      }

      const par = `${email}:${loginSUsuario}`;
      const conteudo = this.toBase64Utf8(par);

      const url = `https://esocial-ptrc.elogica.info/login?conteudo=${encodeURIComponent(
        conteudo
      )}`;
      window.open(url, '_blank');
    } catch (e: any) {
      this.erro = e?.message || 'Não foi possível redirecionar. Tente novamente.';
    } finally {
      this.tentando = false;
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const credentials = btoa(`${this.basicUser}:${this.basicPass}`);
    return new HttpHeaders({
      Authorization: `Basic ${credentials}`,
      Accept: 'application/json, text/plain, */*',
    });
  }

  private async buscarEmail(codigoUsuario: string): Promise<string | null> {
    const url = `${this.apiRoot}/seguranca/${encodeURIComponent(codigoUsuario)}`;
    try {
      const headers = this.getAuthHeaders();
      const resp = await this.http
        .get<SegurancaResponse>(url, { headers })
        .pipe(take(1))
        .toPromise();

      const email = resp?.result?.[0]?.email?.trim();
      return email || null;
    } catch (err) {
      console.error('Erro ao buscar email:', err);
      return null;
    }
  }

  private getUtilDecodificado(): any | null {
    try {
      const utilCriptografado = localStorage.getItem('util');
      if (!utilCriptografado) return null;
      const utilDecriptado = new Crypto().decryptUsingAES256(utilCriptografado);
      return JSON.parse(utilDecriptado);
    } catch {
      return null;
    }
  }

  private toBase64Utf8(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let bin = '';
    bytes.forEach((b) => (bin += String.fromCharCode(b)));
    return btoa(bin);
  }
}
